import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNotifications } from 'hooks';
import { ArrowDown, DocumentDownload, DocumentUpload } from 'iconsax-react';

import { useSelector, useTranslation } from 'third-party';

import { useSettings } from 'modules/setting';

import { appSelectors, settingSelectors } from 'selectors';

interface ImportJsonDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (data: string) => void;
}

const ImportJsonDialog: React.FC<ImportJsonDialogProps> = ({
  open,
  onClose,
  onImport,
}) => {
  const ref = useRef<HTMLSelectElement>(null);
  const { t } = useTranslation();
  const { error } = useNotifications();
  const { loadOrganizationEmailTemplate } = useSettings();

  const currentOrganization = useSelector(appSelectors.currentOrganization);
  const selectedTemplate = useSelector(settingSelectors.selectedTemplate);
  const organizations = useSelector(appSelectors.organizations);

  const filteredOrganizations = useMemo(
    () =>
      organizations.filter(it => it.organization.id != currentOrganization?.id),
    [organizations, currentOrganization?.id],
  );

  const [selectedOrganization, setSelectedOrganization] = useState<string>(
    filteredOrganizations[0]?.organization.id || '',
  );

  useEffect(() => {
    if (open) {
      setSelectedOrganization(filteredOrganizations[0]?.organization.id || '');
    }
  }, [open, filteredOrganizations]);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      error(t('emailDesigner.error.invalidFile'));
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const content = e.target?.result as string;
        JSON.parse(content);
        onImport(content);
        onClose();
      } catch {
        error(t('emailDesigner.error.invalidFile'));
      }
    };
    reader.readAsText(file);
  };

  const handleImportOrganizationChange = (event: SelectChangeEvent) => {
    setSelectedOrganization(event.target.value);
  };

  const handleOrganizationImport = async () => {
    const { payload } = await loadOrganizationEmailTemplate(
      selectedOrganization,
      selectedTemplate,
    );

    onImport(payload.value);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('emailDesigner.import.selectFileTitle')}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Button
            variant="contained"
            component="label"
            startIcon={<DocumentDownload variant="TwoTone" size="24" />}
          >
            {t('emailDesigner.import.selectFile')}
            <input
              type="file"
              hidden
              accept=".json"
              onChange={handleFileUpload}
            />
          </Button>
          {organizations.length > 1 && (
            <Typography variant="h3">
              {t('emailDesigner.import.fromOrganization')}
            </Typography>
          )}
          <Select
            ref={ref}
            size="small"
            variant="standard"
            defaultValue={filteredOrganizations[0]?.organization.id || ''}
            labelId="demo-select-small-label"
            id="demo-select-small"
            displayEmpty
            onChange={handleImportOrganizationChange}
            sx={{
              minWidth: '10rem',
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
            }}
          >
            {filteredOrganizations.map(it => (
              <MenuItem key={it.organization.id} value={it.organization.id}>
                {it.organization.name}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="outlined"
            component="label"
            onClick={handleOrganizationImport}
            startIcon={<ArrowDown variant="TwoTone" size="24" />}
          >
            {t('emailDesigner.import.buttonFromOrganization')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.form.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const ImportTemplate = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { error, success } = useNotifications();
  const { setEmailTemplate } = useSettings();
  const loadEmailTemplatePending = useSelector(
    settingSelectors.loadEmailTemplatePending,
  );
  const saveSettingPending = useSelector(settingSelectors.saveSettingPending);

  const disabledImport = saveSettingPending || loadEmailTemplatePending;

  const handleImport = (content: string) => {
    try {
      setEmailTemplate(JSON.parse(content));
      success(t('emailDesigner.import.senseful'));
    } catch {
      error(t('emailDesigner.error.invalidFile'));
    }
  };

  return (
    <>
      <Tooltip title={t('emailDesigner.actions.importTemplate')}>
        <IconButton disabled={disabledImport} onClick={() => setOpen(true)}>
          <DocumentUpload variant="TwoTone" size="20" />
        </IconButton>
      </Tooltip>

      <ImportJsonDialog
        open={open}
        onClose={() => setOpen(false)}
        onImport={handleImport}
      />
    </>
  );
};
